import { useCallback, useContext, useRef } from "react";
import { match } from "ts-pattern";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import { ETab, TabContext, TabProvider } from "./contexts/Tab";
import Tabs from "./Tabs";
import { CastingForum } from "./CastingForum";
import { CastingVote } from "./CastingVote";
import { CastingList } from "./CastingList";
import classes from "./classes.module.scss";

export function CastingContent() {
	const { isActiveTab } = useContext(TabContext);
	const { onHeaderButtonClick, headerButtonLabel, castingForumRef, castingVoteRef, castingListRef } = useHeaderButton();

	return (
		<div className={classes["root"]}>
			<Typography type="h3" weight="bold" className={classes["title"]}>
				{I18n.asset.pages.professional_space.tabs.casting.title}
			</Typography>

			<div className={classes["menu"]}>
				<div className={classes["tabs-container"]}>
					<Tabs />
				</div>
				<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} onClick={onHeaderButtonClick}>
					{headerButtonLabel()}
				</Button>
			</div>

			{isActiveTab(ETab.FORUM) && <CastingForum ref={castingForumRef} />}
			{isActiveTab(ETab.VOTE) && <CastingVote ref={castingVoteRef} />}
			{isActiveTab(ETab.CASTING) && <CastingList ref={castingListRef} />}
		</div>
	);
}

export default function Casting() {
	return (
		<TabProvider defaultTab={ETab.FORUM}>
			<CastingContent />
		</TabProvider>
	);
}

const useHeaderButton = () => {
	const { activeTab } = useContext(TabContext);

	const castingForumRef = useRef<{ onOpenCreateModal: () => void } | null>(null);
	const castingVoteRef = useRef<{ onOpenCreateModal: () => void } | null>(null);
	const castingListRef = useRef<{ onOpenCreateModal: () => void } | null>(null);

	const onHeaderButtonClick = useCallback(() => {
		match(activeTab)
			.with(ETab.FORUM, () => {
				if (castingForumRef.current) castingForumRef.current.onOpenCreateModal();
			})
			.with(ETab.VOTE, () => {
				if (castingVoteRef.current) castingVoteRef.current.onOpenCreateModal();
			})
			.with(ETab.CASTING, () => {
				if (castingListRef.current) castingListRef.current.onOpenCreateModal();
			})
			.exhaustive();
	}, [activeTab]);

	const headerButtonLabel = useCallback(() => {
		return match(activeTab)
			.with(ETab.FORUM, () => I18n.asset.pages.professional_space.tabs.forum.create_post)
			.with(ETab.VOTE, () => I18n.asset.pages.professional_space.tabs.votes.create_vote)
			.with(ETab.CASTING, () => I18n.asset.pages.professional_space.tabs.casting.create_casting)
			.exhaustive();
	}, [activeTab]);

	return { onHeaderButtonClick, headerButtonLabel, castingForumRef, castingVoteRef, castingListRef };
};
